import React, { useState, useMemo, useCallback, useEffect } from 'react';
import clsx from 'clsx';

import IconSearch from '@/assets/icons/icon-search.svg?react';
import IconArrowUpDown from '@/assets/icons/icon-arrow-up-down.svg?react';
import IconSlash from '@/assets/icons/icon-slash.svg?react';

import { type MetricKey, METRIC_KEYS, METRIC_LABELS } from '@/components/analytics/types';
import { parseNumericValue, getBarWidthPercent } from '@/components/analytics/utils';
import { ListItemRow } from '@/components/analytics/ListItemRow/ListItemRow';
import { InlineSearch } from '@/components/analytics/InlineSearch/InlineSearch';

export interface TableViewCardItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  views: string | number;
  uv: string | number;
  cv: string | number;
  cvr: string | number;
}

interface TableViewCardProps {
  /** Title area — can be a string or a ReactNode for custom headers */
  title: React.ReactNode;
  /** Items to display as rows */
  items: TableViewCardItem[];
  /** Message shown in the empty state placeholder */
  emptyStateMessage?: string;
  /** Enable built-in search filtering */
  searchable?: boolean;
  /** Aria-label for the search button */
  searchLabel?: string;
  /** Enable built-in sort toggle */
  sortable?: boolean;
  /** Extra actions rendered after built-in search/sort buttons */
  headerActions?: React.ReactNode;
  /** Externally controlled metric; component syncs internal state to this */
  activeMetric?: MetricKey;
  /** Callback when a metric tab is clicked */
  onMetricChange?: (metric: MetricKey) => void;
  /** Whether the card has a selected (focused) outline */
  selected?: boolean;
  /** Callback when the card is clicked */
  onSelect?: () => void;
  /** Callback when a row checkbox is toggled */
  onItemSelect?: (key: string, selected: boolean) => void;
  /** Set of currently selected item keys */
  selectedItems?: Set<string>;
  /** Tailwind bg class for the progress bar */
  barColorClass?: string;
  /** Additional class names for the outer container */
  className?: string;
  /** Override class names for the scrollable content area */
  contentClassName?: string;
}

export const TableViewCard: React.FC<TableViewCardProps> = ({
  title,
  items,
  emptyStateMessage = 'No data available',
  searchable = false,
  searchLabel = 'Search',
  sortable = false,
  headerActions,
  activeMetric = 'views',
  onMetricChange,
  selected = false,
  onSelect,
  onItemSelect,
  selectedItems,
  barColorClass = 'bg-violet-200/30',
  className,
  contentClassName,
}) => {
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>(activeMetric);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

  useEffect(() => {
    setSelectedMetric(activeMetric);
  }, [activeMetric]);

  const handleMetricChange = useCallback(
    (metric: MetricKey) => {
      setSelectedMetric(metric);
      onMetricChange?.(metric);
    },
    [onMetricChange],
  );

  const handleSearchClose = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
  }, []);

  const filteredItems = useMemo(() => {
    let result = items;
    if (searchable && searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((item) => item.label.toLowerCase().includes(q));
    }
    if (sortable) {
      result = [...result].sort((a, b) => {
        const aVal = parseNumericValue(a[selectedMetric]);
        const bVal = parseNumericValue(b[selectedMetric]);
        return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
      });
    }
    return result;
  }, [items, searchable, searchQuery, sortable, sortOrder, selectedMetric]);

  const maxValue = useMemo(() => {
    if (!filteredItems.length) return 0;
    return Math.max(...filteredItems.map((item) => parseNumericValue(item[selectedMetric])));
  }, [filteredItems, selectedMetric]);

  const hasData = items.length > 0;
  const hasValues =
    filteredItems.length > 0 &&
    filteredItems.some((item) => parseNumericValue(item[selectedMetric]) > 0);

  const showSearch = searchable && !searchOpen;
  const showSort = sortable;
  const hasBuiltInActions = showSearch || showSort || (searchable && searchOpen);
  const hasAnyActions = hasBuiltInActions || !!headerActions;

  const titleContent = searchable ? (
    <div
      className={clsx(
        'grid min-w-0 flex-1 gap-2 transition-[grid-template-columns] duration-200 ease-out',
        searchOpen ? 'grid-cols-[auto_1fr]' : 'grid-cols-[auto_0fr]',
      )}
    >
      <div className="flex items-center">{title}</div>
      <div className="flex min-w-0 overflow-hidden" inert={!searchOpen ? true : undefined}>
        <InlineSearch value={searchQuery} onChange={setSearchQuery} onClose={handleSearchClose} />
      </div>
    </div>
  ) : (
    title
  );

  return (
    <div
      className={clsx(
        'bg-neutral-0 flex min-h-96 flex-col items-start justify-start gap-1 self-stretch overflow-hidden rounded-xl pb-3 shadow-[0px_4px_12px_0px_rgba(20,27,36,0.04)] outline-1 -outline-offset-1',
        selected ? 'outline-indigo-500' : 'outline-neutral-100',
        className,
      )}
      onClick={onSelect}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onKeyDown={
        onSelect
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect();
              }
            }
          : undefined
      }
    >
      {/* Header */}
      <div className="bg-neutral-0 flex flex-col items-start justify-start self-stretch">
        <div className="inline-flex h-12 items-center justify-between gap-2 self-stretch rounded-tl-md rounded-tr-md py-2 pr-5 pl-4">
          <div className="flex min-w-0 flex-1 items-center">{titleContent}</div>
          {hasAnyActions && (
            <div className="flex shrink-0 items-center gap-1">
              {searchable && !searchOpen && (
                <button
                  type="button"
                  aria-label={searchLabel}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchOpen(true);
                  }}
                  className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-50"
                >
                  <IconSearch className="h-4 w-4" />
                </button>
              )}
              {sortable && (
                <button
                  type="button"
                  aria-label="Toggle sort order"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
                  }}
                  className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-50"
                >
                  <IconArrowUpDown
                    className={clsx(
                      'h-3.5 w-3.5 transition-transform duration-200',
                      sortOrder === 'asc' && 'rotate-180',
                    )}
                  />
                </button>
              )}
              {headerActions}
            </div>
          )}
        </div>

        {/* Metric tabs */}
        <div className="inline-flex items-center justify-end self-stretch overflow-hidden pr-5 pb-1 pl-2">
          <div className="flex h-7 w-48 items-center justify-between rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
            {METRIC_KEYS.map((key) => {
              const isActive = selectedMetric === key;
              return (
                <button
                  key={key}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMetricChange(key);
                  }}
                  className={clsx(
                    'flex flex-1 cursor-pointer items-center justify-center self-stretch rounded-md py-1.5 transition-colors',
                    isActive && 'bg-neutral-0 outline-1 -outline-offset-1 outline-neutral-100',
                  )}
                >
                  <span
                    className={clsx(
                      'text-[10px] leading-5 font-semibold',
                      isActive ? 'text-indigo-500' : 'text-neutral-400',
                    )}
                  >
                    {METRIC_LABELS[key]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      {!hasData ? (
        <div className="flex flex-1 self-stretch overflow-hidden px-3">
          <div className="flex flex-1 flex-col items-center justify-center gap-1 rounded-xl bg-indigo-600/10 px-4 py-7">
            <IconSlash className="h-6 w-6 text-indigo-400" />
            <span className="text-center text-[10px] leading-5 font-semibold text-neutral-500">
              {emptyStateMessage}
            </span>
          </div>
        </div>
      ) : (
        <div
          className={clsx(
            'flex flex-1 flex-col items-start justify-start gap-2 self-stretch overflow-y-auto pb-1 [scrollbar-width:none]',
            contentClassName,
          )}
        >
          {filteredItems.map((item, idx) => (
            <ListItemRow
              key={`${item.key}-${idx}`}
              icon={item.icon}
              label={item.label}
              views={item.views}
              uv={item.uv}
              cv={item.cv}
              cvr={item.cvr}
              activeMetric={selectedMetric}
              barWidthPercent={hasValues ? getBarWidthPercent(item[selectedMetric], maxValue) : 0}
              barColorClass={barColorClass}
              selectable={!!onItemSelect}
              selected={selectedItems?.has(item.key) ?? false}
              onSelect={(sel) => onItemSelect?.(item.key, sel)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
